import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL.replace(/^http:\/\//i, 'https://');

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${API_URL}/api/notifications/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const notifs = data.notifications || [];
        setNotifications(notifs);
        setUnread(notifs.filter((n) => !n.read).length);
      }
    } catch { /* silent */ }
  };

  const handleClick = async (notif) => {
    // Mark as read
    if (!notif.read) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/api/notifications/mark-read/${notif.notification_id}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* silent */ }
    }

    setOpen(false);

    // Navigate to the linked page
    const link = notif.link || '/dashboard';
    navigate(link);
    loadNotifications();
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/notifications/mark-all-read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      loadNotifications();
    } catch { /* silent */ }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Jetzt';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  const typeIcons = {
    tip_result: { color: 'bg-green-500', label: 'Tipp' },
    tip_new: { color: 'bg-[#00BFA6]', label: 'Neu' },
    challenge: { color: 'bg-purple-500', label: 'Challenge' },
    rank_change: { color: 'bg-yellow-500', label: 'Rang' },
    odds_alert: { color: 'bg-orange-500', label: 'Alert' },
    digest: { color: 'bg-blue-500', label: 'Digest' },
    social: { color: 'bg-pink-500', label: 'Social' },
    general: { color: 'bg-gray-500', label: 'Info' },
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
      >
        <Bell className="w-5 h-5 text-gray-400" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-12 w-80 max-h-96 bg-[#1E1E1E] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span className="text-sm font-bold text-white">Benachrichtigungen</span>
            {unread > 0 && (
              <button
                data-testid="mark-all-read-btn"
                onClick={markAllRead}
                className="text-xs text-[#00BFA6] hover:underline"
              >
                Alle lesen
              </button>
            )}
          </div>

          <div className="overflow-y-auto max-h-80">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">
                Keine Benachrichtigungen
              </div>
            ) : (
              notifications.map((notif, i) => {
                const typeInfo = typeIcons[notif.type] || typeIcons.general;
                return (
                  <button
                    key={notif.notification_id || i}
                    data-testid={`notification-item-${i}`}
                    onClick={() => handleClick(notif)}
                    className={`w-full text-left px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 cursor-pointer ${
                      !notif.read ? 'bg-[#00BFA6]/5' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${!notif.read ? 'bg-[#00BFA6]' : 'bg-transparent'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${typeInfo.color} text-white font-medium`}>
                            {typeInfo.label}
                          </span>
                          <span className="text-[10px] text-gray-500">{timeAgo(notif.created_at)}</span>
                        </div>
                        <div className="text-sm text-white font-medium truncate">{notif.title}</div>
                        <div className="text-xs text-gray-400 truncate">{notif.body}</div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
